import { AdminProfessionals } from '@platform/client';
import { useState } from 'react';
import { Link, useParams } from 'wouter';
import {
  AdminViewPageAction,
  ReviewItem,
  ReviewItemShort,
  ReviewPageIconItem,
  ReviewPageWrapper,
} from '../../AdminViewPage';
import { SingleProAppStatusModal } from './UpdateProApplicationStatusModal';
import { UpdateCohortModal } from './UpdateCohortModal';
import { getProBadgeColor } from './ProfessionalsAdmin';
import { ResetUserModal } from '../../components/ResetUserModal';
import { DisableUserModal } from './DisableUserModal';
import { ReviewProAssignedProject } from '@platform/client/schemas';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';

export const ProfessionalReview = () => {
  const { professionalId } = useParams();
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [updateCohortModalOpen, setUpdateCohortModalOpen] = useState(false);
  const [resetUserModalOpen, setResetUserModalOpen] = useState(false);
  const [disableUserModalOpen, setDisableUserModalOpen] = useState(false);

  const {
    data: professional,
    isLoading,
    isError,
    isSuccess,
  } = AdminProfessionals.useGetProfessionalReviewData(Number(professionalId));

  if (!professional) {
    return null;
  }
  const headerIconItems: ReviewPageIconItem[] = [
    {
      icon: 'mail-01',
      label: 'Email',
      value: <a href={`mailto:${professional.email}`}>{professional.email}</a>,
    },
  ];

  if (professional.city || professional.country) {
    headerIconItems.unshift({
      icon: 'marker-pin-01',
      label: 'Location',
      value: [professional.city, professional.country].filter(Boolean).join(', '),
    });
  }

  if (professional.corporateClient) {
    headerIconItems.push({
      icon: 'user-01',
      label: 'Corporate Partner',
      value: professional.corporateClient,
    });
  }

  if (professional.cohortName) {
    headerIconItems.push({
      icon: 'users-02',
      label: 'Cohort Name',
      value: professional.cohortName,
    });
  }

  const pageActions: AdminViewPageAction[] = [
    {
      type: 'button',
      text: 'Reset',
      onPress: () => setResetUserModalOpen(true),
    },
    {
      type: 'button',
      text: 'Disable',
      isDanger: true,
      onPress: () => setDisableUserModalOpen(true),
    },
  ];
  if (
    professional.corporateClient &&
    professional.corporateClient !== 'Ernst & Young' &&
    professional.applicationStatus
  ) {
    pageActions.unshift({
      type: 'button',
      text: 'Update application',
      onPress: () => setUpdateStatusModalOpen(true),
    });
  }
  if (professional.cohortName) {
    pageActions.unshift({
      type: 'button',
      text: 'Update cohort',
      onPress: () => setUpdateCohortModalOpen(true),
    });
  }

  return (
    <ReviewPageWrapper
      title={professional.name}
      headerIconItems={headerIconItems}
      headerLinks={[
        { label: 'LinkedIn', url: professional.linkedin },
        { label: 'Resume', url: professional.resume },
      ]}
      pageActions={pageActions}
      badge={{
        text: professional.applicationStatus?.split('_').join(' ').toUpperCase() ?? 'N/A',
        color:
          professional.applicationStatus ?
            getProBadgeColor(professional.applicationStatus)
          : 'gray',
      }}
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      backNavigation={{
        label: 'Back to professionals',
        href: `~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROFESSIONALS}`,
      }}
    >
      <EmploymentSection employer={professional.employer} jobTitle={professional.jobTitle} />
      <ReviewItemShort
        label='Experience'
        value={professional.experienceYears ? `${professional.experienceYears} years` : undefined}
      />
      <SkillsSection skills={professional.skills} explanation={professional.skillsExplanation} />
      <ReviewItemShort label='Languages' value={professional.languages?.join(', ')} />
      <ReviewItemShort label='Causes' value={professional.causes?.join(', ')} />
      <ReviewItemShort label='Industries' value={professional.industries?.join(', ')} />
      <MatchSection
        matches={professional.assignedProjects}
        introCount={professional.introsCount}
        teamOptIn={professional.teamOptIn}
      />
      {updateStatusModalOpen && professional.applicationId && professional.applicationStatus && (
        <SingleProAppStatusModal
          onClose={() => setUpdateStatusModalOpen(false)}
          applicationId={professional.applicationId as number}
          status={professional.applicationStatus}
          userId={professional.userId}
          isSapPro={professional.corporateClient === 'SAP'}
          queryKeyToInvalidate={AdminProfessionals.getGetProfessionalReviewDataQueryKey(
            professional.userId,
          )}
        />
      )}
      {updateCohortModalOpen && professional.cohortName && professional.corporateClient && (
        <UpdateCohortModal
          userId={professional.userId}
          onClose={() => setUpdateCohortModalOpen(false)}
          currentCohort={professional.cohortName}
          currentPartner={professional.corporateClient || ''}
          proName={professional.name}
          queryKeyToInvalidate={AdminProfessionals.getGetProfessionalReviewDataQueryKey(
            professional.userId,
          )}
        />
      )}
      {resetUserModalOpen && (
        <ResetUserModal
          onClose={() => setResetUserModalOpen(false)}
          userId={professional.userId}
          currentAccountType={'experteer'}
          currentAppStatus={professional.applicationStatus}
        />
      )}
      {disableUserModalOpen && (
        <DisableUserModal
          userId={professional.userId}
          onClose={() => setDisableUserModalOpen(false)}
        />
      )}
    </ReviewPageWrapper>
  );
};

const SkillsSection = ({
  skills,
  explanation,
}: {
  skills?: string[] | null;
  explanation?: string | null;
}) => {
  return (
    <div className='flex flex-col gap-1'>
      <ReviewItemShort label='Skills' value={skills ? skills.join(', ') : 'Not provided'} />
      {explanation && <p>{explanation}</p>}
    </div>
  );
};

const EmploymentSection = ({
  employer,
  jobTitle,
}: {
  employer?: string | null;
  jobTitle?: string | null;
}) => {
  let employmentText = employer;
  if (jobTitle) {
    employmentText = employmentText ? `${jobTitle} at ${employmentText}` : jobTitle;
  }
  return (
    <div className='flex flex-col gap-1'>
      <ReviewItemShort label='Employment' value={employmentText ?? 'Not provided'} />
    </div>
  );
};

const MatchSection = (props: {
  matches?: ReviewProAssignedProject[] | null;
  introCount: number;
  teamOptIn?: boolean | null;
}) => {
  const { matches, introCount, teamOptIn } = props;
  const teamOptInText =
    teamOptIn === undefined ? 'Not answered'
    : teamOptIn ? 'Yes'
    : 'No';
  return (
    <>
      <ReviewItemShort label='Team opt-in' value={teamOptInText} />
      <ReviewItemShort label='Introductions' value={introCount} />
      {matches && matches.length > 0 ?
        <ReviewItem label='Assigned projects'>
          <ul>
            {matches.map((match) => (
              <MatchedProject project={match} key={match.projectId} />
            ))}
          </ul>
        </ReviewItem>
      : <ReviewItemShort label='Assigned projects' value='No assigned projects' />}
    </>
  );
};

const MatchedProject = ({ project }: { project: ReviewProAssignedProject }) => {
  return (
    <li className='flex items-center gap-2'>
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.PROJECTS}/review/${project.projectId}`}
        className='text-brand-500 hover:underline max-w-1/2 truncate'
      >
        {project.name}
      </Link>
      {project.isTeam ?
        <span> (Team) </span>
      : null}
      {project.startDate && project.endDate ?
        <p>
          {new Date(project.startDate ?? '').toLocaleDateString()} -{' '}
          {new Date(project.endDate ?? '').toLocaleDateString()}
        </p>
      : <p>dates not available</p>}
    </li>
  );
};
